// List of secondary images (URLs): add, remove, reorder. Used in product families and sourcing items.
// Supports: upload file, fetch from URL (with resize), or paste direct URL.
// When an image is from our Supabase bucket, offers "Eliminar del storage" to delete the file and remove from list.

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Download, Trash2, ChevronUp, ChevronDown, ImageIcon } from 'lucide-react';
import { uploadFileToSupabase, fetchResizeAndUpload, type ImageFolder } from '@/lib/image-utils';
import { isSupabaseStorageUrl } from '@/lib/store-images';
import { postWorkerApi } from '@/lib/worker-api';
import { useToast } from '@/hooks/use-toast';

export interface SecondaryImagesListProps {
  value: string[];
  onChange: (urls: string[]) => void;
  folder: ImageFolder;
  maxSizeBytes?: number;
  /** When set, each image shows "Usar como portada" to promote it to main/cover. */
  onSetAsCover?: (url: string) => void;
}

export function SecondaryImagesList({
  value,
  onChange,
  folder,
  maxSizeBytes: directUploadMaxSize = 5 * 1024 * 1024,
  onSetAsCover,
}: SecondaryImagesListProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const { toast } = useToast();

  // Handle direct file upload
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file.');
      return;
    }
    if (file.size > directUploadMaxSize) {
      setUploadError(`File too large (max ${Math.round(directUploadMaxSize / 1024 / 1024)}MB).`);
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const publicUrl = await uploadFileToSupabase(file, folder);
      onChange([...value, publicUrl]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  // Fetch image from URL, resize, and upload
  async function handleFetchFromUrl() {
    const url = urlInput.trim();
    if (!url) {
      setUploadError('Please enter a URL');
      return;
    }

    setUploadError(null);
    setFetchingUrl(true);

    try {
      const publicUrl = await fetchResizeAndUpload(url, folder);
      onChange([...value, publicUrl]);
      setUrlInput('');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setFetchingUrl(false);
    }
  }

  function setItem(i: number, url: string) {
    const next = [...value];
    next[i] = url;
    onChange(next);
  }

  function remove(i: number) {
    onChange(value.filter((_, j) => j !== i));
  }

  function moveUp(i: number) {
    if (i <= 0) return;
    const next = [...value];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  }

  function moveDown(i: number) {
    if (i >= value.length - 1) return;
    const next = [...value];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    onChange(next);
  }

  async function handleDeleteFromStorage(i: number) {
    const url = value[i];
    if (!url?.trim() || !isSupabaseStorageUrl(url)) return;
    if (!window.confirm('¿Eliminar esta imagen del storage y quitarla de la lista? Esta acción no se puede deshacer.')) return;
    setDeletingIndex(i);
    try {
      const result = await postWorkerApi<{ ok?: boolean }>('/api/delete-storage-image', { url });
      if (!result.ok) throw new Error(result.error || `Error ${result.status}`);
      remove(i);
      toast({ title: 'Imagen eliminada', description: 'Se ha eliminado del storage y de la lista.' });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo eliminar', variant: 'destructive' });
    } finally {
      setDeletingIndex(null);
    }
  }

  const isProcessing = uploading || fetchingUrl;

  return (
    <div className="space-y-3 min-w-0">
      <Label>Secondary images (gallery)</Label>

      {/* Upload options */}
      <div className="flex flex-col gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200 min-w-0">
        {/* Row 1: Upload file button */}
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={isProcessing}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={isProcessing}
            className="gap-1.5"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Upload file'}
          </Button>
        </div>

        {/* Row 2: Fetch from URL */}
        <div className="flex gap-2 items-center min-w-0">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Paste image URL to fetch & resize…"
            className="flex-1 min-w-0 bg-white"
            disabled={isProcessing}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleFetchFromUrl}
            disabled={isProcessing || !urlInput.trim()}
            className="gap-1.5 shrink-0"
          >
            <Download className="h-3.5 w-3.5" />
            {fetchingUrl ? 'Fetching…' : 'Fetch & Upload'}
          </Button>
        </div>
      </div>

      {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}

      {/* Image list: thumbnail + URL input constrained so long links don't overflow modal */}
      {value.length > 0 && (
        <div className="space-y-2 min-w-0">
          {value.map((url, i) => (
            <div key={i} className="flex gap-2 items-center p-2 bg-white rounded-lg border border-zinc-200 min-w-0">
              {/* Thumbnail only; no URL text shown to avoid overflow */}
              {url && (
                <img src={url} alt="" className="h-14 w-14 object-contain rounded border border-zinc-200 bg-zinc-50 shrink-0" onError={() => {}} />
              )}
              <Input
                value={url}
                onChange={(e) => setItem(i, e.target.value)}
                placeholder="https://… or use upload above"
                className="flex-1 min-w-0 text-sm overflow-hidden"
                title={url || undefined}
              />
              {/* Actions */}
              <div className="flex gap-1 shrink-0 items-center">
                <Button type="button" variant="ghost" size="icon" onClick={() => moveUp(i)} disabled={i === 0} title="Move up" className="h-8 w-8">
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => moveDown(i)} disabled={i >= value.length - 1} title="Move down" className="h-8 w-8">
                  <ChevronDown className="h-4 w-4" />
                </Button>
                {onSetAsCover && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onSetAsCover(url)}
                    title="Usar como portada"
                    className="h-8 gap-1 text-xs"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    Usar como portada
                  </Button>
                )}
                {url && isSupabaseStorageUrl(url) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteFromStorage(i)}
                    disabled={deletingIndex === i}
                    title="Eliminar del storage y quitar de la lista"
                    className="h-8 text-destructive hover:text-destructive text-xs"
                  >
                    {deletingIndex === i ? '…' : 'Eliminar del storage'}
                  </Button>
                )}
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} title="Quitar de la lista" className="h-8 w-8 text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add another */}
      {value.length > 0 && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, ''])}>
          Add another image slot
        </Button>
      )}
    </div>
  );
}
